/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getToken } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface WalletBalance {
  id: number;
  user_id: number;
  balance: number;
  total_earned: number;
  total_spent: number;
}

export interface WalletTransaction {
  id: number;
  wallet_id: number;
  transaction_type: string;
  source: string;
  amount: number;
  balance_after: number;
  description: string | null;
  reference_id: string | null;
  created_at: string;
}

export interface ReferralInfo {
  referral_code: string;
  total_referrals: number;
  completed_referrals: number;
  total_credits_earned: number;
  referrals: {
    id: number;
    referee_email: string | null;
    referee_phone: string | null;
    status: string;
    referrer_credit: number;
    referrer_credited: boolean;
    completed_at: string | null;
    created_at: string;
  }[];
}

interface WalletState {
  balance: WalletBalance | null;
  transactions: WalletTransaction[];
  referralInfo: ReferralInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: WalletState = {
  balance: null,
  transactions: [],
  referralInfo: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchWalletBalance = createAsyncThunk(
  'wallet/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/wallet/balance');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch wallet balance'
      );
    }
  }
);

export const fetchWalletTransactions = createAsyncThunk(
  'wallet/fetchTransactions',
  async (
    params?: { page?: number; page_size?: number; source?: string },
    { rejectWithValue }: any = {}
  ) => {
    try {
      const response = await apiClient.get('/wallet/transactions', { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch transactions'
      );
    }
  }
);

export const fetchReferralInfo = createAsyncThunk(
  'wallet/fetchReferralInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/referrals/status');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch referral info'
      );
    }
  }
);

// Slice
const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch balance
    builder.addCase(fetchWalletBalance.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchWalletBalance.fulfilled, (state, action) => {
      state.loading = false;
      state.balance = action.payload;
    });
    builder.addCase(fetchWalletBalance.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch transactions
    builder.addCase(fetchWalletTransactions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchWalletTransactions.fulfilled, (state, action) => {
      state.loading = false;
      // API returns {transactions, total, current_balance} — extract the array
      state.transactions = Array.isArray(action.payload)
        ? action.payload
        : (action.payload?.transactions ?? []);
    });
    builder.addCase(fetchWalletTransactions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch referral info
    builder.addCase(fetchReferralInfo.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchReferralInfo.fulfilled, (state, action) => {
      state.loading = false;
      state.referralInfo = action.payload;
    });
    builder.addCase(fetchReferralInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError } = walletSlice.actions;
export default walletSlice.reducer;
